/**
 * Mock Projects Data
 * 
 * This data represents projects from a Financials/Dimensions system.
 * When enableFinancialsIntegration is true, these projects will be available
 * for selection in work orders, purchase orders, sales orders, etc.
 */

export interface Project {
  code: string;
  name: string;
  description?: string;
  status: "active" | "on-hold" | "completed" | "cancelled";
  startDate?: Date;
  endDate?: Date;
  manager?: string;
  department?: string;
  budget?: number;
  currency?: string;
}

/**
 * Mock projects data
 * In a real integration, this would come from the Financials/Dimensions API
 */
export const mockProjects: Project[] = [
  {
    code: "PRJ-2024-001",
    name: "Q1 Production Expansion",
    description: "Expand production capacity for Q1 2024",
    status: "active",
    startDate: new Date("2024-01-01"),
    endDate: new Date("2024-03-31"),
    manager: "John Smith",
    department: "Manufacturing",
    budget: 500000,
    currency: "GBP",
  },
  {
    code: "PRJ-2024-002",
    name: "Office Refurbishment",
    description: "Complete office renovation and equipment upgrade",
    status: "active",
    startDate: new Date("2024-02-01"),
    endDate: new Date("2024-04-30"),
    manager: "Sarah Johnson",
    department: "Facilities",
    budget: 150000,
    currency: "GBP",
  },
  {
    code: "PRJ-2024-003",
    name: "New Product Line Development",
    description: "Research and development for new ergonomic furniture line",
    status: "active",
    startDate: new Date("2024-01-15"),
    endDate: new Date("2024-12-31"),
    manager: "Michael Chen",
    department: "R&D",
    budget: 750000,
    currency: "GBP",
  },
  {
    code: "PRJ-2024-004",
    name: "Warehouse Automation",
    description: "Implement automated storage and retrieval systems",
    status: "active",
    startDate: new Date("2024-03-01"),
    endDate: new Date("2024-09-30"),
    manager: "Emily Davis",
    department: "Operations",
    budget: 1200000,
    currency: "GBP",
  },
  {
    code: "PRJ-2024-005",
    name: "Sustainability Initiative",
    description: "Reduce carbon footprint and implement green manufacturing",
    status: "active",
    startDate: new Date("2024-01-01"),
    endDate: new Date("2024-12-31"),
    manager: "David Wilson",
    department: "Sustainability",
    budget: 300000,
    currency: "GBP",
  },
  {
    code: "PRJ-2023-010",
    name: "Legacy System Migration",
    description: "Migrate from old ERP to new cloud-based system",
    status: "completed",
    startDate: new Date("2023-06-01"),
    endDate: new Date("2023-12-31"),
    manager: "Lisa Anderson",
    department: "IT",
    budget: 450000,
    currency: "GBP",
  },
  {
    code: "PRJ-2024-006",
    name: "Customer Portal Development",
    description: "Build customer self-service portal",
    status: "on-hold",
    startDate: new Date("2024-02-01"),
    endDate: new Date("2024-06-30"),
    manager: "Robert Taylor",
    department: "IT",
    budget: 200000,
    currency: "GBP",
  },
  {
    code: "PRJ-2024-007",
    name: "Quality Improvement Program",
    description: "Implement Six Sigma quality control processes",
    status: "active",
    startDate: new Date("2024-01-01"),
    endDate: new Date("2024-06-30"),
    manager: "Jennifer Martinez",
    department: "Quality",
    budget: 100000,
    currency: "GBP",
  },
  {
    code: "PRJ-2024-008",
    name: "Supply Chain Optimization",
    description: "Optimize supplier relationships and reduce lead times",
    status: "active",
    startDate: new Date("2024-02-15"),
    endDate: new Date("2024-08-31"),
    manager: "Thomas Brown",
    department: "Procurement",
    budget: 250000,
    currency: "GBP",
  },
  {
    code: "PRJ-2024-009",
    name: "Employee Training Program",
    description: "Comprehensive training for new manufacturing processes",
    status: "active",
    startDate: new Date("2024-03-01"),
    endDate: new Date("2024-05-31"),
    manager: "Amanda White",
    department: "HR",
    budget: 75000,
    currency: "GBP",
  },
];

/**
 * Get active projects only
 */
export const getActiveProjects = (): Project[] => {
  return mockProjects.filter(p => p.status === "active");
};

/**
 * Get project by code
 */
export const getProjectByCode = (code: string): Project | undefined => {
  return mockProjects.find(p => p.code === code);
};

/**
 * Search projects by code or name
 */
export const searchProjects = (query: string): Project[] => {
  const lowerQuery = query.toLowerCase();
  return mockProjects.filter(
    p =>
      p.code.toLowerCase().includes(lowerQuery) ||
      p.name.toLowerCase().includes(lowerQuery)
  );
};
